import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-hot-toast'

import {
  BaseView,
  BaseSafeFormView,
  HeaderNavigation,
  BaseText,
} from '@/components'
import { OTPInput } from '@/components/OTPInput/OTPInput'
import { ResendCountdown } from '@/components/ResendCountdown'
import { useMFACodeVerification, useStartMFA } from '@/domain/mfa'
import useOffsets from '@/hooks/useOffsets'

export interface MFACodeValidatorScreenParams {
  backRoute: string
  challengeId: string
  requestPayload: Record<string, unknown>
}

const CELL_COUNT = 4

export const MFACodeValidatorScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()

  const paramsData = location.state as MFACodeValidatorScreenParams | undefined

  const { backRoute, requestPayload } = paramsData || {}

  const [challengeId, setChallengeId] = useState(paramsData?.challengeId || '')
  const [codes, setCodes] = useState<string[]>(Array(CELL_COUNT).fill(''))
  const [errorMessage, setErrorMessage] = useState('')

  const { mutateAsync: verifyCode, isPending: isCheckingCode } =
    useMFACodeVerification({
      onSuccess: (data) => {
        setCodes(Array(CELL_COUNT).fill(''))

        navigate(backRoute || '/withdraw/register-wallet-address', {
          state: {
            token: data.token,
          },
        })
      },
      onError: (error) => {
        const { errorMessage } = error

        setErrorMessage(errorMessage || t('api:errors.somethingWrong'))
        setCodes(Array(CELL_COUNT).fill(''))
      },
    })

  const { mutateAsync: startMFA, isPending: isLoadingStartMFA } = useStartMFA({
    onSuccess: ({ challenge_id }) => {
      setChallengeId(challenge_id)
      toast.success(t('common:codeSent'))
    },
    onError: ({ errorMessage }) => {
      toast.error(errorMessage ?? t('api:errors.somethingWrong'))
    },
  })

  const handleCodeVerification = async (fullCode: string) => {
    if (isCheckingCode) {
      return
    }

    await verifyCode({
      challengeId,
      code: fullCode,
    })
  }

  const handleResendCode = async () => {
    if (!requestPayload) {
      toast.error(t('api:errors.somethingWrong'))
      return
    }

    await startMFA({
      mfa_method: 'email',
      action: 'withdrawal_address_add',
      request_payload: requestPayload,
    })
  }

  useEffect(() => {
    if (!paramsData || !backRoute) {
      navigate('/dashboard', { replace: true })
    }
  }, [paramsData, backRoute, navigate])

  if (!paramsData || !backRoute) {
    return null
  }

  return (
    <BaseSafeFormView
      style={{ paddingTop: offsets.top, paddingLeft: 13, paddingRight: 13 }}
    >
      <HeaderNavigation />

      <BaseView flex={1} pt={24} gap={20}>
        <BaseView gap={20}>
          <BaseText fontSize={32} fontFamily="semibold" color="label.default">
            {t('bottomSheets:mfaCodeVerification.title')}
          </BaseText>
          <BaseText fontSize={16} fontFamily="medium" color="label.description">
            {t('bottomSheets:mfaCodeVerification.description')}
          </BaseText>
        </BaseView>

        <BaseView mt={24} gap={50} center fullWidth>
          <OTPInput
            codes={codes}
            setCodes={setCodes}
            onSubmit={handleCodeVerification}
            isSubmitting={isCheckingCode || isLoadingStartMFA}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            autoFocus
            autoFocusAfterClean
            numberCells={CELL_COUNT}
            variant="filled"
          />

          <ResendCountdown onPress={handleResendCode} />
        </BaseView>
      </BaseView>
    </BaseSafeFormView>
  )
}

export default MFACodeValidatorScreen
